/**
 * Refactor ToDo:
 * - Move CitiboxSdkInfo type to ApiWebApp when this file is refactored to .ts
 */
import * as Sentry from '@sentry/nextjs';

import { getEnv, getWebappVersion, isClient, isTestingEnv } from '@/app/_utils/env';
import { sendNewEvent } from '@/app/_utils/events';
import { DeepParseValue, deepParse } from '@/app/_utils/misc';
import { ApiWebApp } from '@/lib/citibox/sdk/ApiWebApp';
import { getItem, setItem } from '@/lib/storage';
import { getCompanyInfoId } from '@/lib/tokenUtils';

const dsn = 'https://b53abd3c9a29a57a2063415f7b261bf5@o378284.ingest.us.sentry.io/4508380979986432';
const release = 'couriers-webapp@' + getWebappVersion();

// Nothing in this file may report its own failures: a courier handheld inside a building entrance
// hall makes every request to /api/log fail, the rejection reaches the global error listeners, and
// reporting it fails again. That loop was ~49% of this project's Sentry volume (CBX-11201).
const MAX_LOG_ENDPOINT_FAILURES = 3;
const MAX_EVENTS_PER_SIGNATURE = 5;

let logEndpointFailures = 0;
let isReportingError = false;
const eventsPerSignature: Record<string, number> = {};

// Breadcrumb helpers

export const sendSentryBreadcrumb = (
  category: string,
  message: string,
  data: Record<string, unknown>,
  level: 'debug' | 'info' | 'warning' | 'error' = 'error'
) => {
  const sanitazer = (value: DeepParseValue, key: string) => {
    const invalidKeys = [
      'Authorization',
      'email',
      'name',
      'password',
      'phone',
      'recipient_hash',
      'recipient_phone',
      'recipient_phone_hash',
      'recipientHash',
      'recipientPhone',
      'recipientPhoneHash',
      'surname',
      'userPhone'
    ];
    return invalidKeys.includes(key) ? 'sanitazed-value' : value;
  };
  const sanitizedData = deepParse(data, sanitazer) as Record<string, DeepParseValue>;
  Sentry.addBreadcrumb({ category, message, level, data: sanitizedData });
  if (getEnv() !== 'local') {
    // eslint-disable-next-line no-console
    console.log('sentryBreadcrumb', { category, message, level, data: JSON.stringify(sanitizedData) });
  }
};

// Error helpers

type SentryEvent = {
  message: string;
  name: string;
  data: Record<string, unknown>;
  date: string;
  stack: string;
  isClient: boolean;
};

type SendSentryErrorOptions = {
  // Set to false when Sentry already reported the error on its own (its global onerror and
  // unhandledrejection handlers do), so it isn't sent twice. The breadcrumb and the
  // /api/log entry are still written.
  captureInSentry?: boolean;
};

export const sendSentryError = (message: string | Error, data: Record<string, unknown> = {}, { captureInSentry = true }: SendSentryErrorOptions = {}) => {
  // Reentrancy guard: anything that throws while we are reporting reaches the global `error` and
  // `unhandledrejection` listeners, which call this function again. Drop instead of looping.
  if (isReportingError) {
    return;
  }
  isReportingError = true;
  try {
    const err = {
      data,
      message: typeof message === 'string' ? message : message.message,
      name: typeof message === 'string' ? 'Error' : message.name,
      stack: typeof message === 'string' ? captureCallerStack() : message.stack,
      isClient: isClient(),
      env: getEnv(),
      date: new Date().toISOString()
    };
    sendNewEvent('sentry', err, (ev: SentryEvent) => captureSentryEvent(ev, captureInSentry));
    if (isClient()) {
      sendNewEvent('opentelemetry', err, postLogEvent);
    }
    // eslint-disable-next-line no-console
    console.log(err);
  } catch {
    // Reporting an error must never throw. There is nowhere left to report this to.
  } finally {
    isReportingError = false;
  }
};

const captureSentryEvent = (ev: SentryEvent, captureInSentry: boolean) => {
  try {
    sendSentryBreadcrumb('sendSentryError', ev.message, ev);
    if (captureInSentry) {
      Sentry.captureException(restoreError(ev));
    }
  } catch {
    // Swallow: see sendSentryError.
  }
};

/**
 * Stack of whoever called `sendSentryError`, for the cases where only a message was passed. Without
 * excluding our own frames the trace starts inside this file, so unrelated call sites end up
 * grouped into the same Sentry issue.
 */
const captureCallerStack = (): string | undefined => {
  if (typeof Error.captureStackTrace === 'function') {
    const holder: { stack?: string } = {};
    Error.captureStackTrace(holder, sendSentryError);
    return holder.stack;
  }
  return new Error().stack;
};

/**
 * Rebuilds the original error. The event round-trips through localStorage (see `sendNewEvent`), so
 * the Error instance itself cannot survive, but copying `name` and `stack` back keeps the real
 * frames. `new Error(ev.message)` used to discard them, which is why so many issues in this project
 * have a stack pointing at this file instead of at the code that actually failed.
 */
const restoreError = (ev: SentryEvent): Error => {
  const error = new Error(ev.message);
  if (ev.name) {
    error.name = ev.name;
  }
  if (ev.stack) {
    error.stack = ev.stack;
  }
  return error;
};

/**
 * Posts the event to /api/log. Rejecting lets `sendNewEvent` keep the event queued and retry it,
 * which matters on handhelds that spend most of a delivery offline. The rejection is always awaited
 * there, so it never becomes an unhandled rejection, and after MAX_LOG_ENDPOINT_FAILURES
 * consecutive failures we stop trying for the rest of the session.
 */
const postLogEvent = async (ev: SentryEvent): Promise<void> => {
  if (logEndpointFailures >= MAX_LOG_ENDPOINT_FAILURES) {
    return;
  }
  try {
    const response = await fetch('/api/log', {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message: ev.message, severity: 'ERROR', date: ev.date, data: ev })
    });
    if (!response.ok) {
      throw new Error(`/api/log responded ${response.status}`);
    }
    logEndpointFailures = 0;
  } catch (error) {
    logEndpointFailures += 1;
    throw error;
  }
};

// Server and edge config

export const initSentryServerConfig = () => {
  Sentry.init({
    dsn,
    release,
    enabled: !isTestingEnv(),
    environment: getSentyEnv(),
    tracesSampleRate: 0.1,
    beforeSend: limitRepeatedEvents
  });
  Sentry.setTag('webapp-version', getWebappVersion());
};

export const sentryCaptureRequestError = Sentry.captureRequestError;

// Client config

type CitiboxSdkInfo = {
  sdk_version: string;
  nwap_version: string;
};

export const initSentryClientConfig = () => {
  Sentry.init({
    dsn,
    release,
    enabled: !isTestingEnv(),
    environment: getSentyEnv(),
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: getReplaysOnErrorSampleRate(),
    ignoreErrors: ['__firefox__', 'setPhotoOptions failed', 'DarkReader'],
    beforeSend: (event) => identifyUser(limitRepeatedEvents(event))
  });

  import('@sentry/nextjs').then((lazyLoadedSentry) => {
    // Sentry conditionally exports replayIntegration. This causes an eslint error.
    // For that reason we do an asynchronous import.
    Sentry.addIntegration(
      lazyLoadedSentry.replayIntegration({
        blockAllMedia: false,
        maskAllInputs: false,
        maskAllText: false
      })
    );
  });

  // Set webapp, sdk and nwap versions
  Sentry.setTag('webapp-version', getWebappVersion());
  ApiWebApp.addInfoObserver((data: CitiboxSdkInfo) => {
    Sentry.setTag('sdk-version', data.sdk_version || 'unknown');
    Sentry.setTag('nwap-version', data.nwap_version || 'unknown');
  });
};

export const sentryCaptureRouterTransitionStart = async () => {
  return import('@sentry/nextjs').then((lazyLoadedSentry) => {
    // Sentry conditionally exports captureRouterTransitionStart. This causes an eslint error.
    // For that reason we do an asynchronous import.
    return lazyLoadedSentry.captureRouterTransitionStart;
  });
};

export const checkAppVersionConsistency = () => {
  // Send a sentry error if client and server are not in the same version
  setTimeout(async () => {
    try {
      const response = await fetch('/api/config');
      const serverConfig = await response.json();
      if (serverConfig.appVersion !== getWebappVersion()) {
        Sentry.captureException(`Server (${serverConfig.appVersion}) and client (${getWebappVersion()}) versions are not the same`);
      }
    } catch {
      // The handheld is offline or the request failed. This check is best-effort: an unhandled
      // rejection here would be picked up by the global error listeners and reported as an error
      // originating in this file.
    }
    // Wait 5 seconds so as not to slow down the initial loading of the page
  }, 5000);
};

// Event filtering

/**
 * Caps how many times the same error signature leaves a session. The reporting path used to feed
 * itself thousands of identical events per session; this makes any future feedback loop cost a
 * handful of events instead of burning the quota. Different sessions still report independently,
 * so a real spike across couriers stays visible.
 */
const limitRepeatedEvents = (event: Sentry.ErrorEvent): Sentry.ErrorEvent | null => {
  const signature = getEventSignature(event);
  eventsPerSignature[signature] = (eventsPerSignature[signature] || 0) + 1;
  return eventsPerSignature[signature] > MAX_EVENTS_PER_SIGNATURE ? null : event;
};

const getEventSignature = (event: Sentry.ErrorEvent): string => {
  const exception = event.exception?.values?.[0];
  return exception ? `${exception.type}: ${exception.value}` : event.message || 'unknown';
};

// User identification

const SENTRY_DEVICE_ID_KEY = 'sentryDeviceId';

/**
 * Every issue in this project reported "0 users impacted", which makes severity impossible to judge
 * from Sentry and blocks meaningful alerting. We identify the handheld, not the person: the session
 * user id when the courier is logged in, otherwise a device id generated once and kept in
 * localStorage. Neither is PII.
 */
const identifyUser = (event: Sentry.ErrorEvent | null): Sentry.ErrorEvent | null => {
  if (event === null) {
    return null;
  }
  const id = getSentryUserId();
  if (id) {
    event.user = { id, ...event.user };
  }
  return event;
};

const getSentryUserId = (): string => {
  try {
    const userId = getItem('userId');
    if (userId) {
      return userId;
    }
    const storedDeviceId = getItem(SENTRY_DEVICE_ID_KEY);
    if (storedDeviceId) {
      return storedDeviceId;
    }
    const deviceId = newDeviceId();
    setItem(SENTRY_DEVICE_ID_KEY, deviceId);
    return deviceId;
  } catch {
    // localStorage can be unavailable or full. An unidentified event is better than no event.
    return '';
  }
};

const newDeviceId = (): string => {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `device-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

// Client helpers

const getReplaysOnErrorSampleRate = () => {
  // We want to capture with replay 100% of the errors that occur when the user accesses /dropoff/couriers?tracking=...&accessToken=...
  const correosPostalCarrierId = '2';
  const location = window.location;
  if (location.pathname.includes('/couriers/dropoffs')) {
    const locationSearchParams = new URLSearchParams(location.search);
    const accessToken = locationSearchParams.get('access_token');
    if (accessToken) {
      const carrierId = getCompanyInfoId(accessToken);
      if (carrierId === correosPostalCarrierId) {
        return 1; // 100%
      }
    }
  }
  return 0; // 0%
};

const getSentyEnv = () => {
  const env = getEnv();
  return env === 'production' ? 'production' : 'development';
};
